import { useMemo } from 'react';
import { isEmpty } from 'lodash';
import { useFormValues } from '@client/hooks/formik.hooks';
import { useInstanceSelector, useSelector } from '@client/hooks/redux.hooks';
import { getBadgeByName } from '@client/selectors/entities/categories';
import { BeaconStatuses, ProfileTypes } from '@sda/enums';
import { serializeFilters } from '@sda/utils/algolia';
import { getDateTime } from '@sda/utils/date';
import { getSessionUserAdminStatus } from '@client/selectors/helpers/login';
import { FilterOptions, RequiresPAOptions } from '../interfaces';
import type { FilterFormValues } from '../interfaces';
import type { AlgoliaFilter } from '@sda/utils/algolia/interfaces';

const defaultFilters = {
  'publicBeacon.endDate._seconds': {
    type: 'futureDate',
  },
  'publicBeacon.beaconStatus': {
    type: 'string',
    value: BeaconStatuses.ACTIVE as string,
  },
  hasPublicImageUrl: {
    type: 'tag',
  },
};

interface UseFormFilterOptions {
  readonly onlyFilterProfileType?: boolean;
}

export const useFormFilter = (options?: UseFormFilterOptions) => {
  const { onlyFilterProfileType } = options || {};

  const {
    dateRange, searchType, filter: formFilter, hasPA,
    venueType, primaryGenre, requiresPA,
  } = useFormValues<FilterFormValues>();

  const [startDate, endDate] = dateRange;

  const isAdmin = useSelector(getSessionUserAdminStatus);
  const experiencedBadge = useInstanceSelector(getBadgeByName, searchType === ProfileTypes.ACT ? 'experienced_act' : 'experienced_venue');
  const partnerBadge = useInstanceSelector(getBadgeByName, searchType === ProfileTypes.ACT ? 'partner_act' : 'partner_venue');

  const filterOptions = useMemo(() => {
    let filters: any = {};

    if (!isAdmin) {
      filters = {
        ...filters,
        'publicBeacon.isPublicCall': {
          type: 'boolean',
          value: true,
        },
      };
    }

    if (onlyFilterProfileType) {
      filters = {
        ...filters,
        ...defaultFilters,
        'publicBeacon.profileType': {
          type: 'string',
          value: searchType as string,
        },
      };

      return serializeFilters(filters as Record<string, AlgoliaFilter>);
    }

    if (startDate) {
      filters = {
        ...filters,
        'publicBeacon.startDate._seconds': {
          type: 'after',
          value: getDateTime(startDate).set({ hour: 0, minute: 0, second: 0 }),
          inclusive: true,
        },
      };

      if (!endDate) {
        // Create a range to ensure only the one day is selected
        filters = {
          ...filters,
          'publicBeacon.startDate._seconds': {
            type: 'before',
            value: getDateTime(startDate).set({ hour: 23, minute: 59, second: 59 }),
          },
        };
      }
    }

    if (endDate) {
      filters = {
        ...filters,
        'publicBeacon.startDate._seconds': {
          type: 'before',
          value: getDateTime(endDate).set({ hour: 23, minute: 59, second: 59 }),
          inclusive: true,
        },
      };
    }

    if (hasPA) {
      filters = {
        ...filters,
        hasPA: {
          type: 'tag',
        },
      };
    }

    if (formFilter) {
      if (formFilter.includes(FilterOptions.FEATURED)) {
        filters = {
          ...filters,
          'publicBeacon.isFeatured': {
            type: 'boolean',
            value: true,
          },
        };
      }

      if (formFilter.includes(FilterOptions.EXPERIENCED) && experiencedBadge?.id) {
        filters = {
          ...filters,
          'publicBeacon.profileData.badge': {
            type: 'string',
            value: experiencedBadge.id,
          },
        };
      }

      if (formFilter.includes(FilterOptions.PARTNER) && partnerBadge?.id) {
        filters = {
          ...filters,
          'publicBeacon.profileData.badge': {
            type: 'string',
            value: partnerBadge.id,
          },
        };
      }
    }

    if (requiresPA) {
      if (requiresPA.includes(RequiresPAOptions.YES) && !requiresPA.includes(RequiresPAOptions.NO)) {
        filters = {
          ...filters,
          'publicBeacon.profileData.requiresPA': {
            type: 'boolean',
            value: true,
          },
        };
      } else if (requiresPA.includes(RequiresPAOptions.NO) && !requiresPA.includes(RequiresPAOptions.YES)) {
        filters = {
          ...filters,
          'publicBeacon.profileData.requiresPA': {
            type: 'boolean',
            value: false,
          },
        };
      }
    }

    if (!isEmpty(venueType)) {
      filters = {
        ...filters,
        'publicBeacon.profileData.venueType': {
          type: 'array',
          value: venueType,
          or: true,
        },
      };
    }

    if (!isEmpty(primaryGenre)) {
      filters = {
        ...filters,
        primaryGenre: {
          type: 'filterGroup',
          group: {
            'publicBeacon.profileData.primaryGenre': {
              type: 'array',
              value: primaryGenre,
              or: true,
            },
            'publicBeacon.profileData.secondaryGenre': {
              type: 'array',
              value: primaryGenre,
              or: true,
            },
          },
        },
      };
    }

    const refinedFilters = {
      ...defaultFilters,
      ...filters,
      'publicBeacon.profileType': {
        type: 'string',
        value: searchType as string,
      },
    };

    return serializeFilters(refinedFilters as Record<string, AlgoliaFilter>);
  }, [
    formFilter, searchType, experiencedBadge, partnerBadge,
    startDate, endDate, onlyFilterProfileType, hasPA,
    venueType, primaryGenre, requiresPA, isAdmin,
  ]);

  return filterOptions;
};
